import { warningAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { splitSearchKeywords, wasKeywordsTruncated } from './multiKeywordSearch';
import { escapeRsqlValue } from './rsql';
import { hasSearchOperators, parseSearchQuery, SearchNode } from './searchQueryParser';

export type SearchBarSelector = {
    selector: string;
    comparator?: string;
    arguments?: string[] | (() => string[]);
};

/** Map of comparators to their negated counterparts for NOT queries. */
const NEGATED_COMPARATORS: Record<string, string> = { '=lk=': '=notlk=' };

interface SearchToRsqlOptions {
    /** Selectors whose search is limited to first argument only */
    limitToFirstArgSelectors?: string[];
    /** Custom comparator processor (e.g., for =lknumeric=) */
    processComparator?: (comparator: string, text: string) => { actualComparator: string; processedText: string };
}

/**
 * Convert wildcard characters in a search term to SQL LIKE equivalents.
 * * → % (any number of characters), ? → _ (single character).
 *
 * Note: literal % and _ in user input are NOT escaped because =lk= already wraps
 * values with %..% (contains-search), making them redundant wildcards. Escaping them
 * with backslashes would produce literal backslash characters in SQL since the backend
 * passes values as-is to LIKE without unescaping.
 */
const convertWildcards = (value: string): string => {
    return value.replace(/\*/g, '%').replace(/\?/g, '_');
};

/**
 * Build an RSQL filter for a single leaf node (term or phrase) against all selectors.
 * Wildcards are only expanded for bare terms — inside a quoted phrase `*` and `?` are literal,
 * per the AC that phrase search returns exact matches only.
 */
const buildLeafRsql = (
    value: string,
    negated: boolean,
    isPhrase: boolean,
    selectors: SearchBarSelector[],
    options: SearchToRsqlOptions
): string => {
    const processedValue = isPhrase ? value : convertWildcards(value);
    const filters: string[] = [];
    const defaultProcess = (comp: string, text: string) => ({ actualComparator: comp, processedText: text });
    const processComp = options.processComparator ?? defaultProcess;

    selectors.forEach((selector) => {
        const baseComparator = selector.comparator ?? '=lk=';
        const { actualComparator, processedText } = processComp(baseComparator, processedValue);

        // For NOT terms: use the negated comparator if one exists. For comparators without
        // a known negated counterpart (e.g. ==, =phonelk=), the NOT is silently ignored as
        // graceful degradation — the term is included with the original comparator.
        const comparator = negated ? (NEGATED_COMPARATORS[actualComparator] ?? actualComparator) : actualComparator;

        const isLimitedSearch = options.limitToFirstArgSelectors?.includes(selector.selector);

        if (selector.arguments) {
            const selectorArgs = typeof selector.arguments === 'function' ? selector.arguments() : selector.arguments;
            const escapedValue = escapeRsqlValue(processedText);
            const allArgs = [...selectorArgs, escapedValue.trim()].map((arg) => `"${arg}"`).join(',');
            filters.push(`${selector.selector}${comparator}(${allArgs})`);
        } else if (isLimitedSearch) {
            const escapedValue = escapeRsqlValue(processedText);
            if (escapedValue.trim()) {
                filters.push(`${selector.selector}${comparator}"${escapedValue.trim()}"`);
            }
        } else {
            const escapedValue = escapeRsqlValue(processedText);
            filters.push(`${selector.selector}${comparator}"${escapedValue.trim()}"`);
        }
    });

    if (filters.length === 0) return '';

    // Negated terms: AND across fields (excluded if ANY field matches → all must NOT match)
    // Non-negated terms: OR across fields
    const joiner = negated ? ';' : ',';
    return `(${filters.join(joiner)})`;
};

/**
 * Recursively convert an AST into an RSQL filter string.
 */
const nodeToRsql = (node: SearchNode, selectors: SearchBarSelector[], options: SearchToRsqlOptions): string => {
    switch (node.type) {
        case 'term':
        case 'phrase':
            return buildLeafRsql(node.value, node.negated, node.type === 'phrase', selectors, options);
        case 'and': {
            const left = nodeToRsql(node.left, selectors, options);
            const right = nodeToRsql(node.right, selectors, options);
            if (!left) return right;
            if (!right) return left;
            return `(${left};${right})`;
        }
        case 'or': {
            const left = nodeToRsql(node.left, selectors, options);
            const right = nodeToRsql(node.right, selectors, options);
            if (!left) return right;
            if (!right) return left;
            return `(${left},${right})`;
        }
    }
};

/**
 * Convert a parsed search AST into an RSQL filter string.
 */
export const searchQueryToRsql = (
    ast: SearchNode,
    selectors: SearchBarSelector[],
    options: SearchToRsqlOptions = {}
): string => {
    const rsql = nodeToRsql(ast, selectors, options);
    return rsql || '';
};

/**
 * Simplified version for cases where selectors are just field name strings (e.g., CasesTab).
 * Uses =lk= comparator and no arguments.
 */
export const searchQueryToSimpleRsql = (ast: SearchNode, fields: string[], comparator: string = '=lk='): string => {
    const selectors: SearchBarSelector[] = fields.map((f) => ({ selector: f, comparator }));
    return searchQueryToRsql(ast, selectors);
};

/**
 * Legacy keyword search: splits input on whitespace (capped at MAX_SEARCH_KEYWORDS),
 * ORs each keyword across all selectors, ANDs the resulting keyword blocks. Used as the
 * fallback when the operator grammar fails to parse.
 */
export const buildLegacySearchRsql = (
    searchText: string,
    selectors: SearchBarSelector[],
    options: SearchToRsqlOptions = {}
): string => {
    const keywords = splitSearchKeywords(searchText);
    if (keywords.length === 0) return '';

    const defaultProcess = (comp: string, text: string) => ({ actualComparator: comp, processedText: text });
    const processComp = options.processComparator ?? defaultProcess;

    const keywordBlocks = keywords.map((keyword) => {
        const keywordFilters: string[] = [];
        selectors.forEach((selector) => {
            const baseComparator = selector.comparator ?? '=lk=';
            const { actualComparator, processedText } = processComp(baseComparator, keyword);
            const isLimitedSearch = options.limitToFirstArgSelectors?.includes(selector.selector);
            const escapedValue = escapeRsqlValue(processedText).trim();

            if (selector.arguments) {
                const selectorArgs =
                    typeof selector.arguments === 'function' ? selector.arguments() : selector.arguments;
                const allArgs = [...selectorArgs, escapedValue].map((arg) => `"${arg}"`).join(',');
                keywordFilters.push(`${selector.selector}${actualComparator}(${allArgs})`);
            } else if (isLimitedSearch) {
                if (escapedValue) {
                    keywordFilters.push(`${selector.selector}${actualComparator}"${escapedValue}"`);
                }
            } else {
                keywordFilters.push(`${selector.selector}${actualComparator}"${escapedValue}"`);
            }
        });
        return `(${keywordFilters.join(',')})`;
    });
    return `(${keywordBlocks.join(';')})`;
};

/**
 * Build RSQL from free-text search input. Routes through the operator-aware parser when the
 * input contains grammar (quotes, AND/OR/NOT, wildcards) and falls back to the legacy
 * whitespace-split builder otherwise or on parse failure. Also surfaces the "first N keywords
 * used" warning when truncation happens on either path, so call sites do not repeat it.
 */
export const buildRsqlFromSearchText = (
    searchText: string,
    selectors: SearchBarSelector[],
    options: SearchToRsqlOptions = {}
): string => {
    if (!searchText.trim()) return '';

    if (hasSearchOperators(searchText)) {
        try {
            const { ast, truncated } = parseSearchQuery(searchText);
            if (truncated) {
                warningAlert(t('search-keywords-truncated'));
            }
            return searchQueryToRsql(ast, selectors, options);
        } catch {
            // Fall through to legacy below — user's input had operators but grammar was invalid.
        }
    }

    if (wasKeywordsTruncated(searchText)) {
        warningAlert(t('search-keywords-truncated'));
    }
    return buildLegacySearchRsql(searchText, selectors, options);
};
